const FALLBACK = 'http://localhost:5000';

/**
 * Base URL for REST + Socket.IO.
 * If you open the UI as http://192.168.x.x:3000 but .env points at localhost:5000,
 * requests go to the client device’s loopback — portal screenshots never arrive.
 *
 * If REACT_APP_API_URL points at another host (e.g. https://aidemo.srufy.com) than the page
 * (e.g. https://demoai.utvmiljo.se), that URL wins — no reverse proxy on the UI host.
 * Backend must allow the UI origin in CORS (see backend server.js DEFAULT_CORS_ORIGINS).
 *
 * Same-host override: hosts in REACT_APP_SAME_ORIGIN_HOSTS (default: *.utvmiljo.se demo hosts) use
 * window.location.origin when the baked-in REACT_APP_API_URL would be unsafe or wrong — e.g.
 * HTTPS page + REACT_APP_API_URL=http://localhost:5000 (mixed content + wrong machine). Then
 * reverse-proxy /api on the page origin is used.
 */
export function getApiBaseUrl() {
  const env = (process.env.REACT_APP_API_URL || '').trim();

  if (typeof window === 'undefined') {
    return env || FALLBACK;
  }

  const host = window.location.hostname;
  const pageIsLoopback = host === 'localhost' || host === '127.0.0.1';

  if (env) {
    try {
      const normalized = env.includes('://') ? env : `https://${env}`;
      const apiUrl = new URL(normalized);
      const apiHost = apiUrl.hostname.toLowerCase();
      if (apiHost === host.toLowerCase()) {
        return env.replace(/\/$/, '');
      }
      // Cross-origin env URL: do not use if it would break the browser (HTTPS → HTTP) or send
      // prod users to their own localhost (baked-in dev .env).
      const httpsPageHttpApi =
        window.location.protocol === 'https:' && apiUrl.protocol === 'http:';
      const apiIsLoopback = apiHost === 'localhost' || apiHost === '127.0.0.1';
      if (!(httpsPageHttpApi || (apiIsLoopback && !pageIsLoopback))) {
        return env.replace(/\/$/, '');
      }
      // Fall through to same-origin / LAN logic below.
    } catch {
      /* ignore bad URL, fall through */
    }
  }

  const rawSameOriginHosts = process.env.REACT_APP_SAME_ORIGIN_HOSTS;
  const sameOriginHosts =
    rawSameOriginHosts !== undefined
      ? rawSameOriginHosts.split(',').map((h) => h.trim().toLowerCase()).filter(Boolean)
      : ['demoai.utvmiljo.se', 'www.demoai.utvmiljo.se', 'dashboard.utvmiljo.se'];

  if (sameOriginHosts.length && sameOriginHosts.includes(host.toLowerCase())) {
    return window.location.origin;
  }

  let envHost = '';
  if (env) {
    try {
      envHost = new URL(env).hostname;
    } catch {
      envHost = '';
    }
  }
  const envIsLoopback =
    !env ||
    envHost === 'localhost' ||
    envHost === '127.0.0.1';

  // Dev: LAN http://192.168.x.x:3000 with .env pointing at localhost → hit API on same machine IP.
  // Prod HTTPS: never downgrade to http:// on the page host — browsers block mixed content, so
  // check-email fails with a generic “connection” error. Same-origin (reverse-proxy /api) is the usual fix.
  if (!pageIsLoopback && envIsLoopback) {
    if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
      return window.location.origin;
    }
    return `http://${host}:5000`;
  }

  return env || FALLBACK;
}
